import type { AppUserId, AuthProviderName, AuthProviderUserId } from "./ids";

/**
 * Enumerations are declared as literal tuples and their union types derived
 * from them, so the runtime list and the compile-time type can never drift
 * and zod schemas keep their literal types.
 */
export const USER_ROLES = ["user", "admin"] as const;
export type UserRole = (typeof USER_ROLES)[number];

/** Languages the international deployment ships. Independent of deployment region. */
export const LANGUAGE_TAGS = ["en", "zh-CN"] as const;
export type LanguageTag = (typeof LANGUAGE_TAGS)[number];

export const isLanguageTag = (value: unknown): value is LanguageTag =>
  typeof value === "string" &&
  (LANGUAGE_TAGS as readonly string[]).includes(value);

/**
 * The link between an application user and a vendor identity.
 *
 * One app user may accumulate several identity records over time (for example
 * a Firebase record today and a China-region record later). The pair
 * (provider, providerUserId) is unique.
 */
export interface AuthIdentity {
  readonly provider: AuthProviderName;
  readonly providerUserId: AuthProviderUserId;
  readonly appUserId: AppUserId;
  readonly email: string;
  readonly linkedAt: Date;
}

/** Profile fields the account owner is allowed to change themselves. */
export interface EditableUserProfile {
  displayName: string;
  organization: string | null;
  preferredLanguage: LanguageTag;
  /** Free-text, optional. Collected only because applicants ask to be called back. */
  contactPhone: string | null;
}

export interface User extends EditableUserProfile {
  readonly id: AppUserId;
  readonly primaryAuthProvider: AuthProviderName;
  readonly primaryAuthProviderUserId: AuthProviderUserId;
  readonly email: string;
  readonly emailVerified: boolean;
  readonly role: UserRole;
  readonly disabled: boolean;
  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly lastSeenAt: Date | null;
}

/** Shape returned to the account owner. */
export interface UserProfileView {
  id: AppUserId;
  email: string;
  emailVerified: boolean;
  displayName: string;
  organization: string | null;
  contactPhone: string | null;
  preferredLanguage: LanguageTag;
  role: UserRole;
  createdAt: string;
  updatedAt: string;
}

export const toUserProfileView = (user: User): UserProfileView => ({
  id: user.id,
  email: user.email,
  emailVerified: user.emailVerified,
  displayName: user.displayName,
  organization: user.organization,
  contactPhone: user.contactPhone,
  preferredLanguage: user.preferredLanguage,
  role: user.role,
  createdAt: user.createdAt.toISOString(),
  updatedAt: user.updatedAt.toISOString(),
});

/** Shape returned to administrators in list views. */
export interface AdminUserView extends UserProfileView {
  disabled: boolean;
  primaryAuthProvider: AuthProviderName;
  lastSeenAt: string | null;
}

export const toAdminUserView = (user: User): AdminUserView => ({
  ...toUserProfileView(user),
  disabled: user.disabled,
  primaryAuthProvider: user.primaryAuthProvider,
  lastSeenAt: user.lastSeenAt ? user.lastSeenAt.toISOString() : null,
});
